/**
 * BookService - Servicio para obtener libros de dominio público
 * Integra Open Library (metadatos) y Gutendex (contenido descargable)
 */

// Cache para evitar llamadas repetidas
const cache = new Map();
const CACHE_TTL = 30 * 60 * 1000; // 30 minutos

// URLs base - usando proxies locales para evitar CORS
const OPEN_LIBRARY_API = '/api/openlibrary';
const GUTENDEX_API = '/api/gutendex';
const GUTENBERG_FILES = '/api/gutenberg-files';
const COVERS_BASE = 'https://covers.openlibrary.org/b';

/**
 * Obtiene datos del cache si no han expirado
 */
function getFromCache(key) {
    const cached = cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
    }
    cache.delete(key);
    return null;
}

/**
 * Guarda datos en cache
 */
function setCache(key, data) {
    cache.set(key, { data, timestamp: Date.now() });
}

/**
 * Mezcla aleatoriamente un array (Fisher-Yates shuffle)
 */
function shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

/**
 * Busca libros por query en Open Library
 */
async function searchBooks(query, limit = 20) {
    const cacheKey = `search:${query}:${limit}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(
            `${OPEN_LIBRARY_API}/search.json?q=${encodeURIComponent(query)}&limit=${limit}`
        );
        const data = await response.json();

        const books = (data.docs || []).map(doc => normalizeOpenLibraryBook(doc));
        setCache(cacheKey, books);
        return books;
    } catch (error) {
        console.error('Error searching books:', error);
        return [];
    }
}

/**
 * Busca libros por género/subject
 */
async function searchBySubject(subject, limit = 20) {
    const cacheKey = `subject:${subject}:${limit}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(
            `${OPEN_LIBRARY_API}/subjects/${encodeURIComponent(subject.toLowerCase())}.json?limit=${limit}`
        );
        const data = await response.json();

        const books = (data.works || []).map(work => normalizeOpenLibrarySubject(work));
        setCache(cacheKey, books);
        return books;
    } catch (error) {
        console.error('Error searching by subject:', error);
        return [];
    }
}

/**
 * Obtiene detalles de un libro por su work ID de Open Library
 */
async function getBookDetails(workId) {
    const cacheKey = `work:${workId}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        // Obtener datos del work
        const workResponse = await fetch(`${OPEN_LIBRARY_API}/works/${workId}.json`);
        const workData = await workResponse.json();

        // Intentar obtener ediciones para más info
        const editionsResponse = await fetch(`${OPEN_LIBRARY_API}/works/${workId}/editions.json?limit=5`);
        const editionsData = await editionsResponse.json();

        const book = {
            id: workId,
            title: workData.title,
            description: extractDescription(workData.description),
            subjects: workData.subjects || [],
            covers: workData.covers || [],
            coverUrl: workData.covers?.[0]
                ? `${COVERS_BASE}/id/${workData.covers[0]}-L.jpg`
                : null,
            authors: [],
            firstPublishYear: null,
            editions: editionsData.entries?.length || 0
        };

        // Obtener autores
        if (workData.authors) {
            const authorPromises = workData.authors.map(async (a) => {
                try {
                    const authorKey = a.author?.key || a.key;
                    if (authorKey) {
                        const res = await fetch(`${OPEN_LIBRARY_API}${authorKey}.json`);
                        const authorData = await res.json();
                        return authorData.name;
                    }
                } catch (e) {
                    return null;
                }
            });
            book.authors = (await Promise.all(authorPromises)).filter(Boolean);
        }

        // Buscar si hay versión en Gutenberg
        const gutenbergBook = await findGutenbergVersion(book.title, book.authors[0]);
        if (gutenbergBook) {
            book.gutenbergId = gutenbergBook.id;
            book.formats = gutenbergBook.formats;
            book.downloadUrl = gutenbergBook.formats?.['text/html'] ||
                               gutenbergBook.formats?.['application/epub+zip'];
        }

        setCache(cacheKey, book);
        return book;
    } catch (error) {
        console.error('Error getting book details:', error);
        return null;
    }
}

/**
 * Busca libros en Gutendex (Project Gutenberg)
 */
async function searchGutenberg(query, limit = 20, language = 'en') {
    const cacheKey = `gutenberg:${query}:${limit}:${language}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(
            `${GUTENDEX_API}/books?search=${encodeURIComponent(query)}&languages=${language}`
        );
        const data = await response.json();

        const books = (data.results || []).slice(0, limit).map(book => normalizeGutenbergBook(book));
        setCache(cacheKey, books);
        return books;
    } catch (error) {
        console.error('Error searching Gutenberg:', error);
        return [];
    }
}

/**
 * Busca libros en Gutendex por tema
 */
async function searchGutenbergByTopic(topic, limit = 20, language = 'en') {
    const cacheKey = `gutenberg-topic:${topic}:${limit}:${language}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(
            `${GUTENDEX_API}/books?topic=${encodeURIComponent(topic)}&languages=${language}`
        );
        const data = await response.json();

        const books = (data.results || []).slice(0, limit).map(book => normalizeGutenbergBook(book));
        setCache(cacheKey, books);
        return books;
    } catch (error) {
        console.error('Error searching Gutenberg by topic:', error);
        return [];
    }
}

/**
 * Obtiene detalles de libro de Gutenberg por ID
 */
async function getGutenbergBook(id) {
    const cacheKey = `gutenberg-book:${id}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(`${GUTENDEX_API}/books/${id}`);
        const data = await response.json();

        const book = normalizeGutenbergBook(data);
        setCache(cacheKey, book);
        return book;
    } catch (error) {
        console.error('Error getting Gutenberg book:', error);
        return null;
    }
}

/**
 * Busca si existe versión en Gutenberg de un libro
 */
async function findGutenbergVersion(title, author = '') {
    try {
        const searchQuery = author ? `${title} ${author}` : title;
        const response = await fetch(
            `${GUTENDEX_API}/books?search=${encodeURIComponent(searchQuery)}`
        );
        const data = await response.json();

        if (data.results && data.results.length > 0) {
            // Retornar el primer resultado que coincida razonablemente
            return data.results[0];
        }
        return null;
    } catch (error) {
        console.error('Error finding Gutenberg version:', error);
        return null;
    }
}

/**
 * Obtiene el contenido de texto de un libro de Gutenberg
 * Usa URLs directas para evitar problemas de 301 redirects
 */
async function getBookContent(gutenbergId) {
    const cacheKey = `content:${gutenbergId}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    // URLs directas de Project Gutenberg (evita redirects de Gutendex)
    const textUrls = [
        `${GUTENBERG_FILES}/cache/epub/${gutenbergId}/pg${gutenbergId}.txt`,
        `${GUTENBERG_FILES}/files/${gutenbergId}/${gutenbergId}-0.txt`,
        `${GUTENBERG_FILES}/files/${gutenbergId}/${gutenbergId}.txt`,
    ];

    for (const url of textUrls) {
        try {
            const response = await fetch(url);
            if (response.ok) {
                const content = await response.text();
                // Verificar que no sea HTML (página de error)
                if (!content.startsWith('<!DOCTYPE') && !content.startsWith('<html')) {
                    const result = { content, type: 'text' };
                    setCache(cacheKey, result);
                    return result;
                }
            }
        } catch (e) {
            console.log(`Failed to fetch from ${url}:`, e.message);
        }
    }

    console.error('Could not fetch book content for ID:', gutenbergId);
    return null;
}

/**
 * Lista curada de libros de Gutenberg por categoría
 * Evita problemas con la API de Gutendex y garantiza libros legibles
 */
const CURATED_BOOKS = {
    fiction: [
        { id: 1342, title: "Pride and Prejudice", authors: ["Jane Austen"] },
        { id: 84, title: "Frankenstein", authors: ["Mary Shelley"] },
        { id: 1661, title: "The Adventures of Sherlock Holmes", authors: ["Arthur Conan Doyle"] },
        { id: 11, title: "Alice's Adventures in Wonderland", authors: ["Lewis Carroll"] },
        { id: 98, title: "A Tale of Two Cities", authors: ["Charles Dickens"] },
        { id: 1400, title: "Great Expectations", authors: ["Charles Dickens"] },
        { id: 174, title: "The Picture of Dorian Gray", authors: ["Oscar Wilde"] },
        { id: 2701, title: "Moby Dick", authors: ["Herman Melville"] },
        { id: 345, title: "Dracula", authors: ["Bram Stoker"] },
        { id: 1260, title: "Jane Eyre", authors: ["Charlotte Brontë"] },
        { id: 768, title: "Wuthering Heights", authors: ["Emily Brontë"] },
        { id: 46, title: "A Christmas Carol", authors: ["Charles Dickens"] },
        { id: 161, title: "Sense and Sensibility", authors: ["Jane Austen"] },
        { id: 158, title: "Emma", authors: ["Jane Austen"] },
        { id: 105, title: "Persuasion", authors: ["Jane Austen"] },
        { id: 730, title: "Oliver Twist", authors: ["Charles Dickens"] },
        { id: 766, title: "David Copperfield", authors: ["Charles Dickens"] },
        { id: 1023, title: "Bleak House", authors: ["Charles Dickens"] },
        { id: 580, title: "The Scarlet Letter", authors: ["Nathaniel Hawthorne"] },
        { id: 5200, title: "Metamorphosis", authors: ["Franz Kafka"] },
    ],
    poetry: [
        { id: 1065, title: "The Raven and Other Poems", authors: ["Edgar Allan Poe"] },
        { id: 8800, title: "The Sonnets", authors: ["William Shakespeare"] },
        { id: 1321, title: "Paradise Lost", authors: ["John Milton"] },
        { id: 4300, title: "Ulysses", authors: ["James Joyce"] },
        { id: 2600, title: "War and Peace", authors: ["Leo Tolstoy"] },
        { id: 135, title: "Les Misérables", authors: ["Victor Hugo"] },
        { id: 2500, title: "Siddhartha", authors: ["Hermann Hesse"] },
        { id: 1952, title: "The Yellow Wallpaper", authors: ["Charlotte Perkins Gilman"] },
        { id: 23, title: "Narrative of Frederick Douglass", authors: ["Frederick Douglass"] },
        { id: 6130, title: "The Iliad", authors: ["Homer"] },
        { id: 1727, title: "The Odyssey", authors: ["Homer"] },
        { id: 2680, title: "Meditations", authors: ["Marcus Aurelius"] },
        { id: 8492, title: "The King James Bible", authors: ["Various"] },
        { id: 100, title: "Complete Works of Shakespeare", authors: ["William Shakespeare"] },
        { id: 2591, title: "Grimm's Fairy Tales", authors: ["Brothers Grimm"] },
        { id: 932, title: "The Fall of the House of Usher", authors: ["Edgar Allan Poe"] },
    ],
    philosophy: [
        { id: 1232, title: "The Prince", authors: ["Niccolò Machiavelli"] },
        { id: 3600, title: "The Essays of Montaigne", authors: ["Michel de Montaigne"] },
        { id: 1497, title: "The Republic", authors: ["Plato"] },
        { id: 5827, title: "The Problems of Philosophy", authors: ["Bertrand Russell"] },
        { id: 4280, title: "Critique of Pure Reason", authors: ["Immanuel Kant"] },
        { id: 2680, title: "Meditations", authors: ["Marcus Aurelius"] },
        { id: 1656, title: "Apology", authors: ["Plato"] },
        { id: 33283, title: "Thus Spake Zarathustra", authors: ["Friedrich Nietzsche"] },
        { id: 7370, title: "Second Treatise of Government", authors: ["John Locke"] },
        { id: 3207, title: "Leviathan", authors: ["Thomas Hobbes"] },
        { id: 10615, title: "The Social Contract", authors: ["Jean-Jacques Rousseau"] },
        { id: 4705, title: "A Modest Proposal", authors: ["Jonathan Swift"] },
        { id: 1080, title: "On Liberty", authors: ["John Stuart Mill"] },
        { id: 815, title: "Democracy in America", authors: ["Alexis de Tocqueville"] },
        { id: 34901, title: "Beyond Good and Evil", authors: ["Friedrich Nietzsche"] },
        { id: 5740, title: "Tractatus Logico-Philosophicus", authors: ["Ludwig Wittgenstein"] },
    ],
    adventure: [
        { id: 120, title: "Treasure Island", authors: ["Robert Louis Stevenson"] },
        { id: 2148, title: "The Count of Monte Cristo", authors: ["Alexandre Dumas"] },
        { id: 215, title: "The Call of the Wild", authors: ["Jack London"] },
        { id: 74, title: "The Adventures of Tom Sawyer", authors: ["Mark Twain"] },
        { id: 76, title: "Adventures of Huckleberry Finn", authors: ["Mark Twain"] },
        { id: 2166, title: "King Solomon's Mines", authors: ["H. Rider Haggard"] },
        { id: 103, title: "Around the World in Eighty Days", authors: ["Jules Verne"] },
        { id: 164, title: "Twenty Thousand Leagues Under the Sea", authors: ["Jules Verne"] },
        { id: 35, title: "The Time Machine", authors: ["H. G. Wells"] },
        { id: 36, title: "The War of the Worlds", authors: ["H. G. Wells"] },
        { id: 1184, title: "The Count of Monte Cristo (Vol 2)", authors: ["Alexandre Dumas"] },
        { id: 829, title: "Gulliver's Travels", authors: ["Jonathan Swift"] },
        { id: 209, title: "The Turn of the Screw", authors: ["Henry James"] },
        { id: 600, title: "Notes from Underground", authors: ["Fyodor Dostoevsky"] },
        { id: 2554, title: "Crime and Punishment", authors: ["Fyodor Dostoevsky"] },
        { id: 28054, title: "The Brothers Karamazov", authors: ["Fyodor Dostoevsky"] },
        { id: 514, title: "Little Women", authors: ["Louisa May Alcott"] },
        { id: 4363, title: "The Jungle Book", authors: ["Rudyard Kipling"] },
        { id: 1257, title: "The Three Musketeers", authors: ["Alexandre Dumas"] },
        { id: 910, title: "White Fang", authors: ["Jack London"] },
    ],
    mystery: [
        { id: 244, title: "A Study in Scarlet", authors: ["Arthur Conan Doyle"] },
        { id: 2852, title: "The Hound of the Baskervilles", authors: ["Arthur Conan Doyle"] },
        { id: 863, title: "The Mysterious Affair at Styles", authors: ["Agatha Christie"] },
        { id: 1155, title: "The Sign of the Four", authors: ["Arthur Conan Doyle"] },
        { id: 973, title: "The Secret Adversary", authors: ["Agatha Christie"] },
        { id: 2097, title: "The Thirty-Nine Steps", authors: ["John Buchan"] },
        { id: 15229, title: "The Murder on the Links", authors: ["Agatha Christie"] },
        { id: 70, title: "The Strange Case of Dr. Jekyll and Mr. Hyde", authors: ["Robert Louis Stevenson"] },
        { id: 2343, title: "The Memoirs of Sherlock Holmes", authors: ["Arthur Conan Doyle"] },
        { id: 108, title: "The Return of Sherlock Holmes", authors: ["Arthur Conan Doyle"] },
        { id: 834, title: "The Valley of Fear", authors: ["Arthur Conan Doyle"] },
        { id: 3289, title: "The Man Who Was Thursday", authors: ["G. K. Chesterton"] },
        { id: 1695, title: "The Moonstone", authors: ["Wilkie Collins"] },
        { id: 583, title: "The Woman in White", authors: ["Wilkie Collins"] },
        { id: 17763, title: "The Murder of Roger Ackroyd", authors: ["Agatha Christie"] },
        { id: 61, title: "The Communist Manifesto", authors: ["Karl Marx"] },
        { id: 155, title: "The Wonderful Wizard of Oz", authors: ["L. Frank Baum"] },
        { id: 236, title: "The Jungle Book", authors: ["Rudyard Kipling"] },
        { id: 8164, title: "The Man in the Iron Mask", authors: ["Alexandre Dumas"] },
        { id: 2489, title: "Moby Dick", authors: ["Herman Melville"] },
    ],
};

/**
 * Obtiene libros por categoría usando lista curada de Gutenberg
 * Los libros se mezclan aleatoriamente en cada carga
 * Para idiomas distintos a inglés, busca en la API
 */
async function getBooksByCategory(category, language = 'en') {
    // Para inglés, usar lista curada
    if (language === 'en') {
        const books = CURATED_BOOKS[category] || CURATED_BOOKS.fiction;
        return shuffleArray(books).map(book => ({
            id: `gutenberg-${book.id}`,
            gutenbergId: book.id,
            title: book.title,
            authors: book.authors,
            coverUrl: `https://www.gutenberg.org/cache/epub/${book.id}/pg${book.id}.cover.medium.jpg`,
            source: 'gutenberg'
        }));
    }

    // Para otros idiomas, buscar en la API por tema
    const topicMap = {
        fiction: 'fiction',
        classics: 'classic',
        romance: 'romance',
        adventure: 'adventure',
        mystery: 'mystery detective',
        scifi: 'science fiction',
        philosophy: 'philosophy',
        history: 'history'
    };
    const topic = topicMap[category] || category;
    return searchGutenbergByTopic(topic, 20, language);
}

/**
 * Obtiene libro destacado (para el Banner)
 */
async function getFeaturedBook() {
    const featuredIds = [
        1342,   // Pride and Prejudice
        84,     // Frankenstein
        1661,   // Sherlock Holmes
        11,     // Alice in Wonderland
        1232,   // The Prince
        2701,   // Moby Dick
        74,     // Tom Sawyer
        345,    // Dracula
        1260,   // Jane Eyre
        98,     // A Tale of Two Cities
    ];

    // Seleccionar uno aleatorio
    const randomId = featuredIds[Math.floor(Math.random() * featuredIds.length)];

    try {
        const book = await getGutenbergBook(randomId);
        return book;
    } catch (error) {
        console.error('Error getting featured book:', error);
        // Fallback a búsqueda
        const books = await searchGutenberg('classic literature', 1);
        return books[0] || null;
    }
}

// ============ Funciones de normalización ============

/**
 * Normaliza datos de búsqueda de Open Library
 */
function normalizeOpenLibraryBook(doc) {
    return {
        id: doc.key?.replace('/works/', '') || doc.cover_edition_key,
        title: doc.title,
        authors: doc.author_name || [],
        firstPublishYear: doc.first_publish_year,
        coverUrl: doc.cover_i
            ? `${COVERS_BASE}/id/${doc.cover_i}-M.jpg`
            : null,
        subjects: doc.subject?.slice(0, 5) || [],
        editionCount: doc.edition_count || 0,
        hasFulltext: doc.has_fulltext || false,
        source: 'openlibrary'
    };
}

/**
 * Normaliza datos de subject de Open Library
 */
function normalizeOpenLibrarySubject(work) {
    return {
        id: work.key?.replace('/works/', ''),
        title: work.title,
        authors: work.authors?.map(a => a.name) || [],
        firstPublishYear: work.first_publish_year,
        coverUrl: work.cover_id
            ? `${COVERS_BASE}/id/${work.cover_id}-M.jpg`
            : null,
        subjects: [],
        source: 'openlibrary'
    };
}

/**
 * Normaliza datos de Gutenberg
 */
function normalizeGutenbergBook(book) {
    // Only use formats provided by the API - not all books have PDFs
    const formats = { ...(book.formats || {}) };

    return {
        id: `gutenberg-${book.id}`,
        gutenbergId: book.id,
        title: book.title,
        authors: book.authors?.map(a => a.name) || [],
        subjects: book.subjects || [],
        bookshelves: book.bookshelves || [],
        languages: book.languages || [],
        downloadCount: book.download_count || 0,
        coverUrl: book.formats?.['image/jpeg'] || null,
        formats: formats,
        source: 'gutenberg'
    };
}

/**
 * Extrae descripción de diferentes formatos de Open Library
 */
function extractDescription(desc) {
    if (!desc) return '';
    if (typeof desc === 'string') return desc;
    if (desc.value) return desc.value;
    return '';
}

// ============ Internet Archive PDF Search ============

/**
 * Busca un PDF en Internet Archive por título y autor
 * @param {string} title - Título del libro
 * @param {string} author - Autor del libro (opcional)
 * @returns {Promise<string|null>} URL del PDF o null si no existe
 */
async function searchInternetArchivePDF(title, author = '') {
    try {
        // Limpiar título para búsqueda
        const cleanTitle = title.replace(/[^\w\s]/g, '').trim();
        const cleanAuthor = author.replace(/[^\w\s]/g, '').trim();

        // Construir query de búsqueda - excluir libros que requieren préstamo
        let query = `title:(${cleanTitle})`;
        if (cleanAuthor) {
            query += ` AND creator:(${cleanAuthor})`;
        }
        // Solo textos, excluir colección "inlibrary" (préstamos) y "lending"
        query += ' AND mediatype:(texts) AND -collection:(inlibrary) AND -collection:(internetarchivebooks)';

        const searchUrl = `https://archive.org/advancedsearch.php?q=${encodeURIComponent(query)}&fl=identifier&output=json&rows=10`;

        const searchResponse = await fetch(searchUrl);
        if (!searchResponse.ok) return null;

        const searchData = await searchResponse.json();
        const docs = searchData?.response?.docs || [];

        if (docs.length === 0) return null;

        // Buscar PDF en los primeros resultados
        for (const doc of docs) {
            const identifier = doc.identifier;
            const metadataUrl = `https://archive.org/metadata/${identifier}`;

            const metaResponse = await fetch(metadataUrl);
            if (!metaResponse.ok) continue;

            const metaData = await metaResponse.json();

            // Verificar que no sea un libro de préstamo (borrowable)
            const metadata = metaData?.metadata || {};
            if (metadata.access_restricted === true ||
                metadata.lending_available === true ||
                metadata.collection?.includes('inlibrary')) {
                continue; // Saltar libros que requieren préstamo
            }

            const files = metaData?.files || [];

            // Buscar archivo PDF que sea público
            const pdfFile = files.find(f =>
                f.name?.toLowerCase().endsWith('.pdf') &&
                f.format?.toLowerCase().includes('pdf') &&
                f.private !== 'true'
            );

            if (pdfFile) {
                return `https://archive.org/download/${identifier}/${pdfFile.name}`;
            }
        }

        return null;
    } catch (error) {
        console.error('Error searching Internet Archive PDF:', error);
        return null;
    }
}

// ============ Exports ============

const BookService = {
    // Búsqueda
    searchBooks,
    searchBySubject,
    searchGutenberg,
    searchGutenbergByTopic,

    // Detalles
    getBookDetails,
    getGutenbergBook,

    // Contenido
    getBookContent,
    findGutenbergVersion,

    // Categorías
    getBooksByCategory,
    getFeaturedBook,

    // Utilidades
    clearCache: () => cache.clear(),

    // Internet Archive
    searchInternetArchivePDF
};

export default BookService;
